import type { SVGProps } from 'react';

export type IconName =
  | 'calendar'
  | 'clock'
  | 'link'
  | 'share'
  | 'users'
  | 'check'
  | 'plus'
  | 'arrow'
  | 'arrow-l'
  | 'copy'
  | 'x'
  | 'sparkle'
  | 'edit'
  | 'trash'
  | 'chevron-down';

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
}

export function Icon({ name, size = 20, ...rest }: IconProps) {
  const props = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    ...rest,
  };
  switch (name) {
    case 'calendar':
      return (
        <svg {...props}>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
      );
    case 'clock':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
      );
    case 'link':
      return (
        <svg {...props}>
          <path d="M9 17H7A5 5 0 017 7h2M15 7h2a5 5 0 010 10h-2M8 12h8" />
        </svg>
      );
    case 'share':
      return (
        <svg {...props}>
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <path d="M8.59 13.51l6.83 3.98M15.41 6.51L8.59 10.49" />
        </svg>
      );
    case 'users':
      return (
        <svg {...props}>
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
        </svg>
      );
    case 'check':
      return (
        <svg {...props}>
          <path d="M20 6L9 17l-5-5" />
        </svg>
      );
    case 'plus':
      return (
        <svg {...props}>
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case 'arrow':
      return (
        <svg {...props}>
          <path d="M5 12h14M13 5l7 7-7 7" />
        </svg>
      );
    case 'arrow-l':
      return (
        <svg {...props}>
          <path d="M19 12H5M11 5l-7 7 7 7" />
        </svg>
      );
    case 'copy':
      return (
        <svg {...props}>
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
        </svg>
      );
    case 'x':
      return (
        <svg {...props}>
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      );
    case 'sparkle':
      return (
        <svg {...props}>
          <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
        </svg>
      );
    case 'edit':
      return (
        <svg {...props}>
          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      );
    case 'trash':
      return (
        <svg {...props}>
          <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
        </svg>
      );
    case 'chevron-down':
      return (
        <svg {...props}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
    default:
      return null;
  }
}

interface MarkProps {
  size?: number;
}

export function Mark({ size = 28 }: MarkProps) {
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} fill="none" aria-hidden="true">
      <rect x="6" y="12" width="52" height="46" rx="10" fill="#15181F" />
      <rect x="16" y="6" width="6" height="14" rx="3" fill="#15181F" />
      <rect x="42" y="6" width="6" height="14" rx="3" fill="#15181F" />
      <rect x="14" y="32" width="36" height="14" rx="4" fill="#C8FF4A" />
      <circle cx="18" cy="26" r="1.6" fill="#F7F3EA" />
      <circle cx="26" cy="26" r="1.6" fill="#F7F3EA" />
      <circle cx="34" cy="26" r="1.6" fill="#F7F3EA" />
      <circle cx="42" cy="26" r="1.6" fill="#F7F3EA" />
      <path
        d="M22 39 l4 4 l10 -10"
        stroke="#15181F"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface WordmarkProps {
  height?: number;
}

export function Wordmark({ height = 28 }: WordmarkProps) {
  const ratio = 320 / 64;
  return (
    <svg
      viewBox="0 0 320 64"
      height={height}
      width={ratio * height}
      fill="none"
      aria-label="WhomFree"
      role="img"
    >
      <rect x="6" y="12" width="52" height="46" rx="10" fill="#15181F" />
      <rect x="16" y="6" width="6" height="14" rx="3" fill="#15181F" />
      <rect x="42" y="6" width="6" height="14" rx="3" fill="#15181F" />
      <rect x="14" y="32" width="36" height="14" rx="4" fill="#C8FF4A" />
      <circle cx="18" cy="26" r="1.6" fill="#F7F3EA" />
      <circle cx="26" cy="26" r="1.6" fill="#F7F3EA" />
      <circle cx="34" cy="26" r="1.6" fill="#F7F3EA" />
      <circle cx="42" cy="26" r="1.6" fill="#F7F3EA" />
      <path
        d="M22 39 l4 4 l10 -10"
        stroke="#15181F"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="207" y="36" width="68" height="9" rx="2" fill="#C8FF4A" opacity="0.55" />
      <text
        x="78"
        y="44"
        fontFamily="'Bricolage Grotesque', system-ui, sans-serif"
        fontWeight={700}
        fontSize={34}
        letterSpacing="-1"
        fill="#15181F"
      >
        whom<tspan>free</tspan>
      </text>
    </svg>
  );
}
